import { createTrackingConsentBanner } from './banner';
import {
  buildTrackingConsentCookieName,
  hasExactKeys,
  readTrackingConsentDecisions,
  selectTrackingConsentCookieScope,
  writeTrackingConsentDecisions
} from './cookie';

import type {
  CategoryDecisions,
  EffectiveTrackingConsentPolicy,
  ResourceTrackingConsentScope,
  StoredTrackingConsentDecision,
  TrackingConsentCategoryKey,
  TrackingConsentDecision,
  TrackingConsentManager,
  TrackingConsentSettingsButtonPosition,
  TrackingConsentSettingsButtonVariant
} from './types';

const SAVE_ERROR = 'We could not save your privacy choice. Please try again.';

type CreateTrackingConsentManagerOptions = {
  policy: EffectiveTrackingConsentPolicy;
  scope: ResourceTrackingConsentScope;
  document: Document;
  location: Location;
  now?: () => Date;
  showSettingsButton?: boolean;
  settingsButtonPosition?: TrackingConsentSettingsButtonPosition;
  settingsButtonVariant?: TrackingConsentSettingsButtonVariant;
};

export interface GoldcastConsent {
  readonly categories: CategoryDecisions;
  readonly version: string;
}

declare global {
  interface Window {
    goldcastConsent?: GoldcastConsent;
  }
}

export const installGoldcastConsentGlobal = (
  target: Window,
  policy: Pick<EffectiveTrackingConsentPolicy, 'enabled' | 'policy_version'>,
  decisions: Record<string, TrackingConsentDecision>
): void => {
  if (!policy.enabled) {
    delete target.goldcastConsent;
    return;
  }
  const categories = Object.freeze({ ...decisions });
  target.goldcastConsent = Object.freeze({
    categories,
    version: policy.policy_version
  });
};

export const createTrackingConsentManager = ({
  policy,
  scope,
  document,
  location,
  now = () => new Date(),
  showSettingsButton = false,
  // Bottom right is where hosts already park chat and support widgets, and a
  // labelled pill covers more of the page than a 44px disc, so the launcher
  // defaults to the icon in the opposite corner. Hosts can still ask for a
  // corner and a label that suit their own layout.
  settingsButtonPosition = 'bottom-left',
  settingsButtonVariant = 'icon'
}: CreateTrackingConsentManagerOptions): TrackingConsentManager => {
  const controllableCategories = policy.tracker_groups.filter(group => group.controllable).map(group => group.key);
  const uncontrollableCategories = new Set(
    policy.tracker_groups.filter(group => !group.controllable).map(group => group.key)
  );
  const cookieScope = selectTrackingConsentCookieScope(policy, scope);

  const unknownDecisions = (): CategoryDecisions =>
    Object.freeze(Object.fromEntries(controllableCategories.map(key => [key, 'unknown']))) as CategoryDecisions;

  let decisions: CategoryDecisions = policy.enabled
    ? readTrackingConsentDecisions({
        document,
        scope: cookieScope,
        expectedKeys: controllableCategories,
        policyVersion: policy.policy_version,
        preferenceLifetimeDays: policy.preference_lifetime_days,
        now
      })
    : unknownDecisions();
  let saveError: string | null = null;
  let bannerController: ReturnType<typeof createTrackingConsentBanner> | null = null;

  installGoldcastConsentGlobal(window, policy, decisions);

  const ensureBanner = (): ReturnType<typeof createTrackingConsentBanner> => {
    if (!bannerController) {
      bannerController = createTrackingConsentBanner({
        policy,
        document,
        id: buildTrackingConsentCookieName(cookieScope),
        initialError: saveError,
        getDecisions: () => decisions,
        showSettingsButton,
        settingsButtonPosition,
        settingsButtonVariant,
        onAllowAll: () => {
          save(
            Object.fromEntries(controllableCategories.map(key => [key, 'allowed'])) as Record<
              string,
              StoredTrackingConsentDecision
            >
          );
        },
        onRejectAll: () => {
          save(
            Object.fromEntries(controllableCategories.map(key => [key, 'rejected'])) as Record<
              string,
              StoredTrackingConsentDecision
            >
          );
        },
        onSavePreferences: (choices: Record<string, boolean>) => {
          save(
            Object.fromEntries(
              controllableCategories.map(key => [key, choices[key] ? 'allowed' : 'rejected'])
            ) as Record<string, StoredTrackingConsentDecision>
          );
        }
      });
    }

    return bannerController;
  };

  const save = (next: Record<string, StoredTrackingConsentDecision>): boolean => {
    if (!policy.enabled || !hasExactKeys(next, controllableCategories)) {
      return false;
    }
    const saved = writeTrackingConsentDecisions({
      document,
      location,
      scope: cookieScope,
      expectedKeys: controllableCategories,
      decisions: next,
      policyVersion: policy.policy_version,
      preferenceLifetimeDays: policy.preference_lifetime_days,
      now
    });
    if (!saved) {
      decisions = unknownDecisions();
      saveError = SAVE_ERROR;
      bannerController?.showError(SAVE_ERROR);
      installGoldcastConsentGlobal(window, policy, decisions);
      return false;
    }
    decisions = Object.freeze({ ...next }) as CategoryDecisions;
    saveError = null;
    installGoldcastConsentGlobal(window, policy, decisions);
    location.reload();
    return true;
  };

  return {
    get decisions(): CategoryDecisions {
      return decisions;
    },
    get categories(): readonly TrackingConsentCategoryKey[] {
      return Object.freeze([...controllableCategories]);
    },
    get saveError(): string | null {
      return saveError;
    },
    get settingsLabel(): string {
      return policy.banner.settings_label;
    },
    canTrack: (category: TrackingConsentCategoryKey) =>
      !policy.enabled || uncontrollableCategories.has(category) || decisions[category] === 'allowed',
    mountBanner: () => {
      installGoldcastConsentGlobal(window, policy, decisions);
      if (!policy.enabled) {
        return;
      }
      const controller = ensureBanner();
      if (Object.values(decisions).some(decision => decision === 'unknown')) {
        controller.openBanner();
      }
    },
    openSettings: () => {
      if (policy.enabled) {
        ensureBanner().openSettings(decisions);
      }
    },
    allowAll: () =>
      save(
        Object.fromEntries(controllableCategories.map(key => [key, 'allowed'])) as Record<
          string,
          StoredTrackingConsentDecision
        >
      ),
    rejectAll: () =>
      save(
        Object.fromEntries(controllableCategories.map(key => [key, 'rejected'])) as Record<
          string,
          StoredTrackingConsentDecision
        >
      ),
    savePreferences: (choices: Record<TrackingConsentCategoryKey, boolean>) => {
      if (!hasExactKeys(choices, controllableCategories)) {
        return false;
      }
      return save(
        Object.fromEntries(controllableCategories.map(key => [key, choices[key] ? 'allowed' : 'rejected'])) as Record<
          string,
          StoredTrackingConsentDecision
        >
      );
    },
    destroy: () => {
      bannerController?.destroy();
      bannerController = null;
    }
  };
};
